// WCAG 2.0 color contrast calculation https://www.w3.org/TR/WCAG20-TECHS/G18.html
// See also: https://css-tricks.com/converting-color-spaces-in-javascript/

export var hexToRgb = function hexToRgb(hex) {
  var hexDigits = hex.replace(/^#/, '');
  if (hexDigits.length === 3 || hexDigits.length === 4) {
    hexDigits = Array.from(hexDigits, function (c) {
      return c + c;
    }).join('');
  }
  if (!/^([a-f\d]{6}|[a-f\d]{8})$/i.test(hexDigits)) {
    // eslint-disable-next-line no-console
    console.error('Invalid hexadecimal color input');
    return {
      r: 0,
      g: 0,
      b: 0
    };
  }
  var rgb = {
    r: parseInt(hexDigits.slice(0, 2), 16),
    g: parseInt(hexDigits.slice(2, 4), 16),
    b: parseInt(hexDigits.slice(4, 6), 16)
  };
  if (hex.length === 8) {
    rgb.a = parseInt(hexDigits.slice(6, 8), 16) / 255;
  }
  return rgb;
};
export var rgbToHsl = function rgbToHsl(_ref) {
  var r = _ref.r,
    g = _ref.g,
    b = _ref.b;
  var rNormalized = r / 255;
  var gNormalized = g / 255;
  var bNormalized = b / 255;
  var max = Math.max(rNormalized, gNormalized, bNormalized);
  var min = Math.min(rNormalized, gNormalized, bNormalized);
  var h = 0;
  var s = 0;
  var l = 0;
  l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rNormalized:
        h = (gNormalized - bNormalized) / d + (gNormalized < bNormalized ? 6 : 0);
        break;
      case gNormalized:
        h = (bNormalized - rNormalized) / d + 2;
        break;
      case bNormalized:
        h = (rNormalized - gNormalized) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }
  return {
    h: h * 360,
    s: s * 100,
    l: l * 100
  };
};
export var hslToRgb = function hslToRgb(_ref2) {
  var h = _ref2.h,
    s = _ref2.s,
    l = _ref2.l;
  var hNormalized = h < 0 ? 0 : h / 360;
  var sNormalized = s < 0 ? 0 : s / 100;
  var lNormalized = l < 0 ? 0 : l / 100;
  var hueToRgb = function hueToRgb(p, q, t) {
    var t2 = t;
    if (t2 < 0) t2 = t2 + 1;
    if (t2 > 1) t2 = t2 - 1;
    if (t2 < 1 / 6) return p + (q - p) * 6 * t2;
    if (t2 < 1 / 2) return q;
    if (t2 < 2 / 3) return p + (q - p) * (2 / 3 - t2) * 6;
    return p;
  };
  var r;
  var g;
  var b;
  if (sNormalized === 0) {
    r = g = b = lNormalized;
  } else {
    var q = lNormalized < 0.5 ? lNormalized * (1 + sNormalized) : lNormalized + sNormalized - lNormalized * sNormalized;
    var p = 2 * lNormalized - q;
    r = hueToRgb(p, q, hNormalized + 1 / 3);
    g = hueToRgb(p, q, hNormalized);
    b = hueToRgb(p, q, hNormalized - 1 / 3);
  }
  var rgb = {
    r: Math.round(Math.min(r, 1) * 255),
    g: Math.round(Math.min(g, 1) * 255),
    b: Math.round(Math.min(b, 1) * 255)
  };
  return rgb;
};
export var rgbToHex = function rgbToHex(_ref3) {
  var r = _ref3.r,
    g = _ref3.g,
    b = _ref3.b;
  var rHex = r.toString(16);
  var gHex = g.toString(16);
  var bHex = b.toString(16);
  if (rHex.length === 1) {
    rHex = "0".concat(rHex);
  }
  if (gHex.length === 1) {
    gHex = "0".concat(gHex);
  }
  if (bHex.length === 1) {
    bHex = "0".concat(bHex);
  }
  return "#".concat(rHex.toUpperCase()).concat(gHex.toUpperCase()).concat(bHex.toUpperCase());
};
export var linearRgb = function linearRgb(colorComponent) {
  var sRgb = colorComponent / 255;
  return sRgb <= 0.03928 ? sRgb / 12.92 : Math.pow((sRgb + 0.055) / 1.055, 2.4);
};
export var relativeLuminance = function relativeLuminance(rgb) {
  var r = linearRgb(rgb.r);
  var g = linearRgb(rgb.g);
  var b = linearRgb(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export var contrastRatio = function contrastRatio(color1RGB, color2RGB) {
  var lum1 = relativeLuminance(color1RGB) + 0.05;
  var lum2 = relativeLuminance(color2RGB) + 0.05;
  return lum1 > lum2 ? lum1 / lum2 : lum2 / lum1;
};
export var calcAccessibleContrast = function calcAccessibleContrast(color1, color2) {
  var color1ToRgb = hexToRgb(color1);
  var color2ToRgb = hexToRgb(color2);
  return contrastRatio(color1ToRgb, color2ToRgb);
};
export var getHighContrastColor = function getHighContrastColor(color) {
  var white = '#FFFFFF';
  var black = '#000000';
  var contrastWhite = calcAccessibleContrast(color, white);
  var contrastBlack = calcAccessibleContrast(color, black);
  return contrastWhite >= contrastBlack ? white : black;
};
export var getHighlightAndPressColors = function getHighlightAndPressColors(backgroundHex, foregroundHex) {
  var adjustment = 5;
  var foregroundHsl = rgbToHsl(hexToRgb(foregroundHex));
  var backgroundHsl = rgbToHsl(hexToRgb(backgroundHex));
  var foregroundIsLighter = foregroundHsl.l >= backgroundHsl.l;
  var highlight = foregroundIsLighter ? rgbToHex(hslToRgb({
    h: backgroundHsl.h,
    s: backgroundHsl.s,
    l: backgroundHsl.l - adjustment
  })) : rgbToHex(hslToRgb({
    h: backgroundHsl.h,
    s: backgroundHsl.s,
    l: backgroundHsl.l + adjustment
  }));
  var press = foregroundIsLighter ? rgbToHex(hslToRgb({
    h: backgroundHsl.h,
    s: backgroundHsl.s,
    l: backgroundHsl.l - adjustment * 2
  })) : rgbToHex(hslToRgb({
    h: backgroundHsl.h,
    s: backgroundHsl.s,
    l: backgroundHsl.l + adjustment * 2
  }));
  return {
    highlight: highlight,
    press: press
  };
};